import React, { useState, useCallback } from "react"
import { Card, Flex, Button, Select, Input } from "antd"
import { SearchOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import moment from "moment"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import DateSwitcher from "modules/datepicker/DateSwitcher"
import { getJournalIcon } from "pagesV2/pro/Calendar/utils"
import { routes } from "router/routes"
import { useCalendarData } from "./hooks/useCalendarData"
import { useDateNavigation } from "./hooks/useDateNavigation"
import { useScrollControls } from "./hooks/useScrollControls"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import MonthView from "./components/MonthView"
import WeekView from "./components/WeekView"
import DayView from "./components/DayView"
import styles from "./EquipmentCalendar.module.css"
import IconWithBg from "ui/IconWithBg"
import { Text, Title } from "ui/Typography"
import ReservationDetailModal from "pagesV2/pro/Bookings/ReservationDetailPage/ReservationDetailModal"
import Journal from "pagesV2/pro/Calendar/Modals/Journal"

// Asset search dropdown component (extracted to avoid recreation on every render)
const AssetSearchDropdown = ({ menu, assetSearch, onSearchChange, t }) => (
  <>
    <div style={{ padding: "8px" }}>
      <Input
        placeholder={t("equipment.calendar.search_assets", { defaultValue: "Search assets..." })}
        value={assetSearch}
        onChange={(e) => onSearchChange(e.target.value)}
        prefix={<SearchOutlined />}
        allowClear
      />
    </div>
    {menu}
  </>
)

AssetSearchDropdown.propTypes = {
  menu: PropTypes.node.isRequired,
  assetSearch: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired
}

const EquipmentCalendar = ({ equipmentId, equipment }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL)
  const canEditJournal = hasPermission(MODULES.CALENDAR, ACTIONS.EDIT_JOURNAL)
  const canViewJournal = hasPermission(MODULES.CALENDAR, ACTIONS.VIEW_JOURNAL)

  // Local state
  const [activeFilter, setActiveFilter] = useState("with_bookings")
  const [viewMode, setViewMode] = useState("week")
  const [selectedAsset, setSelectedAsset] = useState("all")
  const [assetSearch, setAssetSearch] = useState("")
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)
  const [selectedBookingId, setSelectedBookingId] = useState(null)
  const [isJournalModalOpen, setIsJournalModalOpen] = useState(false)
  const [selectedJournalDate, setSelectedJournalDate] = useState(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // Custom hooks
  const { selectedDate, goToToday, formatJournalDate, setSelectedDate } = useDateNavigation(viewMode)

  const { assets, loading, totalBookingsCount, journalNoteDates } = useCalendarData(
    equipmentId,
    viewMode,
    selectedDate,
    activeFilter,
    selectedAsset,
    refreshTrigger
  )

  const { dayGridRef, showLeftArrow, showRightArrow, handleScroll, handleHeaderScroll } = useScrollControls(viewMode, assets)

  // Calculate date range based on view mode for journal modal
  const getDateRange = useCallback(() => {
    const currentDate = moment(selectedDate)
    const formattedDate = currentDate.format("YYYY-MM-DD")
    switch (viewMode) {
      case "day":
        return { start: formattedDate, end: formattedDate }
      case "week":
        return {
          start: currentDate.clone().startOf("week").format("YYYY-MM-DD"),
          end: currentDate.clone().endOf("week").format("YYYY-MM-DD")
        }
      case "month":
        return {
          start: currentDate.clone().startOf("month").format("YYYY-MM-DD"),
          end: currentDate.clone().endOf("month").format("YYYY-MM-DD")
        }
      default:
        return { start: formattedDate, end: formattedDate }
    }
  }, [selectedDate, viewMode])

  // Memoize dropdown render function to avoid recreation on every render
  const renderAssetSearchDropdown = useCallback(
    (menu) => <AssetSearchDropdown menu={menu} assetSearch={assetSearch} onSearchChange={setAssetSearch} t={t} />,
    [assetSearch, t]
  )

  // Filter tabs
  const filterTabs = [
    { key: "all", label: t("equipment.calendar.all_assets") },
    { key: "with_bookings", label: t("equipment.calendar.assets_with_bookings") },
    { key: "without_bookings", label: t("equipment.calendar.assets_without_bookings") }
  ]

  // View mode buttons
  const viewModes = [
    { key: "day", label: t("equipment.calendar.day") },
    { key: "week", label: t("equipment.calendar.week") },
    { key: "month", label: t("equipment.calendar.month") }
  ]

  // Handle view mode change
  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode)
    // Reset asset filter when switching to month view
    if (mode === "month") {
      setSelectedAsset("all")
    }
  }, [])

  const handleBookingClick = useCallback((bookingUuid) => {
    setSelectedBookingId(bookingUuid)
    setIsBookingModalOpen(true)
  }, [])

  const handleBookingCancel = useCallback(() => {
    setIsBookingModalOpen(false)
    setSelectedBookingId(null)
  }, [])

  const handleJournalClick = useCallback((date) => {
    setSelectedJournalDate(date)
    setIsJournalModalOpen(true)
  }, [])

  const handleJournalCancel = useCallback(() => {
    setIsJournalModalOpen(false)
    setSelectedJournalDate(null)
  }, [])

  const handleJournalSuccess = useCallback(() => {
    // Increment refreshTrigger to force calendar data refetch
    setRefreshTrigger((prev) => prev + 1)
  }, [])

  const renderJournalSection = () => {
    const dateString = moment(selectedDate).format("YYYY-MM-DD")
    const hasJournal = journalNoteDates?.includes(dateString)
    const canShowIcon = (hasJournal && (canEditJournal || canViewJournal)) || (!hasJournal && canCreateJournal)
    return canShowIcon ? (
      <Flex align="center" gap={12} className={styles.journalSection}>
        <Text variant="caption" size="md">
          {formatJournalDate}
        </Text>
        <IconWithBg
          icon={getJournalIcon(journalNoteDates || [], dateString)}
          onClick={() => handleJournalClick(dateString)}
          style={{ cursor: "pointer" }}
        />
      </Flex>
    ) : null
  }

  /**
   * Handle "Add New Booking" button click
   * Navigate to booking creation route with filtered experiences
   */
  const handleAddNewBooking = useCallback(() => {
    // Navigate to booking creation route, passing equipment's assigned experience IDs
    // The filterExperienceIds will be available in location.state
    navigate(routes.guideBookingNewPath(), {
      state: {
        filterExperienceIds: equipment?.trips?.map((trip) => trip.id) || [],
        equipmentId: equipment?.id,
        equipmentName: equipment?.name
      }
    })
  }, [navigate, equipment])

  /**
   * Handle date change from DateSwitcher
   * @param {Object} e - Event object with target.value containing the new date
   */
  const handleDateChange = useCallback(
    (e) => {
      const newDate = moment(e.target.value).toDate()
      setSelectedDate(newDate)
    },
    [setSelectedDate]
  )

  // Render calendar based on view mode
  const renderCalendarGrid = () => {
    if (viewMode === "month") {
      return (
        <MonthView
          selectedDate={selectedDate}
          assets={assets}
          selectedAsset={selectedAsset}
          onBookingClick={handleBookingClick}
          journalNoteDates={journalNoteDates}
          onJournalClick={handleJournalClick}
          canCreateJournal={canCreateJournal}
          canEditJournal={canEditJournal}
          canViewJournal={canViewJournal}
        />
      )
    }

    if (viewMode === "week") {
      return (
        <WeekView
          selectedDate={selectedDate}
          assets={assets}
          onBookingClick={handleBookingClick}
          journalNoteDates={journalNoteDates}
          onJournalClick={handleJournalClick}
          canCreateJournal={canCreateJournal}
          canEditJournal={canEditJournal}
          canViewJournal={canViewJournal}
        />
      )
    }

    return (
      <DayView
        assets={assets}
        dayGridRef={dayGridRef}
        showLeftArrow={showLeftArrow}
        showRightArrow={showRightArrow}
        handleScroll={handleScroll}
        handleHeaderScroll={handleHeaderScroll}
        onBookingClick={handleBookingClick}
      />
    )
  }
  const viewGranularityMap = { month: "month", week: "week", day: "day" }
  const viewGranularity = viewGranularityMap[viewMode] || "day"
  const isOnToday = selectedDate && moment(selectedDate).isSame(moment(), viewGranularity)

  return (
    <Card className={styles.calendarCard}>
      {/* Header */}
      <Flex justify="space-between" align="center" className={styles.header}>
        <Title level={4}>
          {t("equipment.details.upcoming_bookings")} <span className={styles.totalCount}> ({totalBookingsCount})</span>
        </Title>
        {hasPermission("bookings", "create") && (
          <Button type="primary" onClick={handleAddNewBooking}>
            {t("equipment.details.add_new_booking")}
          </Button>
        )}
      </Flex>

      {/* Filter Tabs (Day/Week view) or Asset Selector (Month view) - Only show if multiple assets */}
      {/* {assets.length > 1 && ( */}
      <>
        {viewMode === "month" ? (
          <Flex gap={8} className={styles.filterTabs}>
            <Select
              showSearch={false}
              placeholder={t("equipment.calendar.search_for_asset")}
              value={selectedAsset}
              onChange={setSelectedAsset}
              className={styles.assetSelect}
              style={{ width: "280px" }}
              dropdownRender={renderAssetSearchDropdown}
              optionFilterProp="children"
              filterOption={false}
            >
              <Select.Option value="all">{t("equipment.calendar.all_assets")}</Select.Option>
              {assets
                .filter((asset) => !assetSearch || asset.number.toString().includes(assetSearch.toLowerCase()))
                .map((asset) => (
                  <Select.Option key={asset.id} value={asset.id.toString()}>
                    {t("equipment.calendar.asset")} #{asset.number}
                  </Select.Option>
                ))}
            </Select>
          </Flex>
        ) : (
          <Flex gap={8} className={styles.filterTabs}>
            {filterTabs.map((tab) => (
              <Button
                key={tab.key}
                onClick={() => setActiveFilter(tab.key)}
                className={activeFilter === tab.key ? styles.activeFilterTab : styles.filterTab}
              >
                {tab.label}
              </Button>
            ))}
          </Flex>
        )}
      </>
      {/* )} */}

      {/* Date Navigation and Controls */}
      <Flex justify="space-between" align="center" className={styles.controls}>
        <Flex align="center" gap={16}>
          <DateSwitcher
            className={styles.calendarSwitcher}
            value={moment(selectedDate)}
            defaultValue={moment().startOf("date")}
            name="date"
            onChange={handleDateChange}
            type={viewMode}
            isCompleteDateRange={true}
          />

          {/* View Mode Toggles */}
          <Flex gap={4} className={styles.viewModeToggle}>
            {viewModes.map((mode) => (
              <Button
                key={mode.key}
                onClick={() => handleViewModeChange(mode.key)}
                size="small"
                className={viewMode === mode.key ? styles.activeViewMode : styles.viewModeButton}
              >
                {mode.label}
              </Button>
            ))}
          </Flex>
        </Flex>

        <Flex align="center" gap={12}>
          <Button
            color="primary"
            variant="outlined"
            size="middle"
            disabled={isOnToday}
            onClick={goToToday}
          >
            {t("calendar.go_to_today", "Go to Today")}
          </Button>
        </Flex>
      </Flex>

      {/* Journal Section (Day View Only) */}
      {viewMode === "day" && renderJournalSection()}

      {/* Calendar Grid */}
      <FirstLoading loading={loading}>{renderCalendarGrid()}</FirstLoading>
      {isBookingModalOpen && (
        <ReservationDetailModal open={isBookingModalOpen} onClose={handleBookingCancel} bookingUuid={selectedBookingId} />
      )}
      {isJournalModalOpen && selectedJournalDate && (
        <Journal
          open={isJournalModalOpen}
          setOpen={setIsJournalModalOpen}
          onCancel={handleJournalCancel}
          selectedDate={selectedJournalDate}
          startDate={getDateRange().start}
          endDate={getDateRange().end}
          context="equipment"
          equipmentId={equipmentId}
          onSuccess={handleJournalSuccess}
        />
      )}
    </Card>
  )
}

EquipmentCalendar.propTypes = {
  equipmentId: PropTypes.string.isRequired,
  equipment: PropTypes.object.isRequired
}

export default EquipmentCalendar
